import React, { useState, useEffect } from 'react';
import { Calendar, Globe, Mail, TrendingUp, Package, Clock, CheckCircle, Users } from 'lucide-react';
import { apiService, Domain } from '../services/api';

export const DomainOrderReports: React.FC = () => {
  const [beginDate, setBeginDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    domainsOrdered: 0,
    activeEmails: 0,
    totalEmails: 0,
    verifiedDomains: 0,
    completedUploads: 0,
    pendingDomains: 0,
    averageEmailsPerDomain: 0,
    clientsCount: 0,
    domainsByPlatform: {} as Record<string, number>,
    emailVolumeByPlatform: {} as Record<string, number>,
    smartleadInRange: 0
  });

  // Helper function to format date strings without timezone conversion
  const formatDateString = (dateString: string): string => {
    // Split the date string and format it directly without timezone conversion
    const [year, month, day] = dateString.split('-');
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  // Helper function to safely format dates from the database
  const formatDbDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    // Extract just the date part if it includes time
    const datePart = dateString.split('T')[0];
    return formatDateString(datePart);
  };

  const fetchDomainReport = async () => {
    setLoading(true);
    try {
      // Fetch both domains and clients for the date range
      const [domainsResponse, clientsResponse] = await Promise.all([
        apiService.getDomainsByDateRange(beginDate, endDate),
        apiService.getClientsByDateRange(beginDate, endDate)
      ]);
      
      if (domainsResponse.success) {
        setDomains(domainsResponse.data);
        // Calculate stats but use the actual clients count from the separate API call
        calculateStats(
          domainsResponse.data, 
          clientsResponse.success ? clientsResponse.count : 0,
          domainsResponse.smartlead_in_range ?? undefined
        );
      } else {
        console.error('Failed to fetch domains:', domainsResponse.message);
        setDomains([]);
        calculateStats([], 0);
      }
    } catch (error) {
      console.error('Error fetching domain report:', error);
      setDomains([]);
      calculateStats([], 0);
    } finally {
      setLoading(false);
    }
  };

  const parseAccountsList = (accounts: string | undefined): string[] => {
    if (!accounts) return [];
    
    try {
      // Handle different formats of accounts data
      if (typeof accounts === 'string') {
        // Try parsing as JSON first
        try {
          const parsed = JSON.parse(accounts);
          if (Array.isArray(parsed)) {
            return parsed;
          }
        } catch {
          // If not JSON, try comma-separated
          return accounts.split(',').map(a => a.trim()).filter(a => a);
        }
      }
      return [];
    } catch {
      return [];
    }
  };

  const calculateStats = (domainList: Domain[], clientsCount: number, smartleadInRangeOverride?: number) => {
    let activeEmails = 0;
    let totalEmails = 0;
    let verifiedDomains = 0;
    let completedUploads = 0;
    let pendingDomains = 0;
    const domainsByPlatform: Record<string, number> = {};
    const emailVolumeByPlatform: Record<string, number> = {};
    let smartleadInRange = 0;

    // Helper function to check if a value is truly empty/null
    const isEmpty = (value: string | undefined | null) => {
      return !value || 
             value.trim() === '' || 
             value.toLowerCase() === 'null' || 
             value.toLowerCase() === 'undefined';
    };

    domainList.forEach(domain => {
      // Count emails
      const accountsToCreate = parseAccountsList(domain.accounts_to_create);
      const accountsToCancel = parseAccountsList(domain.accounts_to_cancel);
      
      activeEmails += accountsToCreate.length;
      totalEmails += accountsToCreate.length + accountsToCancel.length;

      // Count domain statuses
      if (domain.verified_connection) verifiedDomains++;
      if (domain.completed_upload) completedUploads++;
      
      // Use consistent pending domain criteria
      if (!domain.verified_connection && 
          !isEmpty(domain.accounts_to_create) &&
          isEmpty(domain.accounts_to_cancel) &&
          !domain.canceled_domain) {
        pendingDomains++;
      }

      // Count by platform
      const platform = domain.platform || 'Unknown';
      domainsByPlatform[platform] = (domainsByPlatform[platform] || 0) + 1;
      emailVolumeByPlatform[platform] = (emailVolumeByPlatform[platform] || 0) + accountsToCreate.length;
      
      // Smartlead count based on included list (now includes Smartlead by created_at via backend)
      if ((domain.platform || '').toLowerCase() === 'smartlead') smartleadInRange++;
    });

    setStats({
      domainsOrdered: domainList.length,
      activeEmails,
      totalEmails,
      verifiedDomains,
      completedUploads,
      pendingDomains,
      averageEmailsPerDomain: domainList.length > 0 ? Math.round(activeEmails / domainList.length * 10) / 10 : 0,
      clientsCount: clientsCount,
      domainsByPlatform,
      emailVolumeByPlatform,
      smartleadInRange: smartleadInRangeOverride ?? smartleadInRange
    });
  };

  useEffect(() => {
    fetchDomainReport();
  }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Domain Order Reports</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          View domain orders and email statistics for a specific date range
        </p>
      </div>

      {/* Date Range Filters */}
      <div className="card-modern p-6">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <label className="text-sm font-medium">Beginning Date</label>
            <input
              type="date"
              value={beginDate}
              onChange={(e) => setBeginDate(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchDomainReport}
              disabled={loading}
              className="btn-primary px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <div className="card-modern p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Domains Ordered</p>
              <p className="text-2xl font-bold mt-1">{stats.domainsOrdered}</p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
              <Globe className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="card-modern p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Clients Created</p>
              <p className="text-2xl font-bold mt-1">{stats.clientsCount}</p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-500">
              <Users className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="card-modern p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active Emails</p>
              <p className="text-2xl font-bold mt-1">{stats.activeEmails}</p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500">
              <Mail className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="card-modern p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Emails</p>
              <p className="text-2xl font-bold mt-1">{stats.totalEmails}</p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500">
              <Package className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="card-modern p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg Emails/Domain</p>
              <p className="text-2xl font-bold mt-1">{stats.averageEmailsPerDomain}</p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-500">
              <TrendingUp className="h-5 w-5" />
            </div>
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="card-modern p-6">
          <h3 className="font-semibold mb-4">Domain Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm">Verified</span>
              </div>
              <span className="text-sm font-medium">{stats.verifiedDomains}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-yellow-500" />
                <span className="text-sm">Pending</span>
              </div>
              <span className="text-sm font-medium">{stats.pendingDomains}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-blue-500" />
                <span className="text-sm">Completed Upload</span>
              </div>
              <span className="text-sm font-medium">{stats.completedUploads}</span>
            </div>
          </div>
        </div>

        <div className="card-modern p-6">
          <h3 className="font-semibold mb-4">Domains by Platform</h3>
          <div className="space-y-3">
            {Object.entries(stats.domainsByPlatform).map(([platform, count]) => (
              <div key={platform} className="flex items-center justify-between">
                <span className="text-sm">{platform}</span>
                <span className="text-sm font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-modern p-6">
          <h3 className="font-semibold mb-2">Smartlead Domains (Between {formatDateString(beginDate)} and {formatDateString(endDate)})</h3>
          <p className="text-2xl font-bold">{stats.smartleadInRange}</p>
          <p className="text-xs text-muted-foreground mt-2">Total Smartlead domains in the selected range</p>
        </div>

        <div className="card-modern p-6">
          <h3 className="font-semibold mb-4">Email Volume by Platform</h3>
          <div className="space-y-3">
            {Object.entries(stats.emailVolumeByPlatform).map(([platform, volume]) => (
              <div key={platform} className="flex items-center justify-between">
                <span className="text-sm">{platform}</span>
                <span className="text-sm font-medium">{volume}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-modern p-6">
          <h3 className="font-semibold mb-4">Report Summary</h3>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>
              Between {formatDateString(beginDate)} and {formatDateString(endDate)}, 
              {' '}{stats.domainsOrdered} domains were ordered with a total of {stats.totalEmails} email accounts.
            </p>
            <p className="mt-2">
              {stats.verifiedDomains} domains ({stats.domainsOrdered > 0 ? Math.round(stats.verifiedDomains / stats.domainsOrdered * 100) : 0}%) 
              have been successfully verified.
            </p>
          </div>
        </div>
      </div>

      {/* Domain List Table */}
      {domains.length > 0 && (
        <div className="card-modern p-6">
          <h3 className="font-semibold mb-4">Domain Details</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-4">Domain</th>
                  <th className="text-left py-2 px-4">Platform</th>
                  <th className="text-left py-2 px-4">Active Emails</th>
                  <th className="text-left py-2 px-4">Status</th>
                  <th className="text-left py-2 px-4">Created</th>
                </tr>
              </thead>
              <tbody>
                {domains.slice(0, 10).map((domain) => (
                  <tr key={domain.id} className="border-b border-border">
                    <td className="py-2 px-4">{domain.domain_name}</td>
                    <td className="py-2 px-4">{domain.platform}</td>
                    <td className="py-2 px-4">{parseAccountsList(domain.accounts_to_create).length}</td>
                    <td className="py-2 px-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        domain.verified_connection 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                      }`}>
                        {domain.verified_connection ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td className="py-2 px-4">{formatDbDate(domain.bubble_creation_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {domains.length > 10 && (
              <p className="text-sm text-muted-foreground mt-2 px-4">
                Showing 10 of {domains.length} domains
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}; 